// Compare the reporting API's existing aggregates; never recalculate sales.
const day = 86400000;
const iso = (date) => date.toISOString().slice(0, 10);
function validDate(value) {
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    Number.isFinite(Date.parse(value)) &&
    iso(new Date(value)) === value
  );
}
function yearToDate(today = iso(new Date())) {
  const date = new Date(`${today}T00:00:00Z`);
  const year = date.getUTCFullYear() - 1;
  const month = date.getUTCMonth();
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return [
    { from: `${today.slice(0, 4)}-01-01`, to: today },
    {
      from: `${year}-01-01`,
      to: iso(
        new Date(Date.UTC(year, month, Math.min(date.getUTCDate(), lastDay)))
      ),
    },
  ];
}
function previousPeriod(from, to) {
  const start = Date.parse(from);
  const length = Date.parse(to) - start + day;
  return {
    from: iso(new Date(start - length)),
    to: iso(new Date(start - day)),
  };
}
function validation(draft) {
  if (draft.kind === "ceremonies") {
    if (!draft.university_slug)
      return "Choose a university, then choose two ceremonies.";
    if (
      !draft.aCeremony ||
      !draft.bCeremony ||
      draft.aCeremony === draft.bCeremony
    )
      return "Choose two different ceremonies explicitly.";
    if (!validDate(draft.cutoff)) return "Enter a valid cutoff date.";
  } else if (draft.kind === "elapsed") {
    if (draft.intervals.some((p) => !Number.isFinite(Date.parse(p.start_at)) ||
        !Number.isFinite(Date.parse(p.end_at)) || Date.parse(p.start_at) >= Date.parse(p.end_at)))
      return "Choose a nonempty reporting interval.";
  } else {
    for (const [index, period] of draft.periods.entries()) {
      if (
        !validDate(period.from) ||
        !validDate(period.to) ||
        period.from > period.to
      )
        return `Period ${
          index === 0 ? "A" : "B"
        } needs valid dates, with the start on or before the end.`;
    }
  }
  return "";
}
function requestParams(draft) {
  const shared = {
    timezone: draft.timezone || "UTC",
    channel: draft.channel,
    include_disputes: String(draft.include_disputes),
    include_ceremonies: "false",
  };
  if (draft.tenant) shared.tenant = draft.tenant;
  if (draft.university_slug) shared.university_slug = draft.university_slug;
  return [0, 1].map((index) =>
    draft.kind === "ceremonies"
      ? {
          ...shared,
          mode: "ceremony",
          cutoff: draft.cutoff,
          ceremony_ids: index ? draft.bCeremony : draft.aCeremony,
        }
      : { ...shared, mode: "activity", ...(draft.kind === "elapsed" ? draft.intervals[index] : draft.periods[index]) }
  );
}
function complete(report) {
  return (
    report.coverage &&
    report.coverage.status === "complete" &&
    !report.coverage.unknown_line_amounts &&
    !report.coverage.unmapped_count
  );
}
function integer(value) {
  return Number.isSafeInteger(value) ? value : null;
}
function metric(label, a, b, comparable, currency = null) {
  const delta = comparable && a !== null && b !== null ? integer(a - b) : null;
  return {
    label,
    a,
    b,
    delta,
    currency,
    percent: delta === null || b === 0 ? null : (delta / Math.abs(b)) * 100,
    reason:
      delta === null
        ? "Not comparable"
        : b === 0
        ? "No baseline"
        : "",
  };
}
function comparisonRows(a, b, includeDisputes = true) {
  const comparable = complete(a) && complete(b);
  const available = (report, value) =>
    report.coverage && report.coverage.status !== "not_synced"
      ? integer(value)
      : null;
  const groups = [
    {
      title: "Orders and products",
      rows: [
        ["orders", "Orders"],
        ["clip_quantity", "Clips"],
        ["digital_download_quantity", "Digital downloads"],
        ["snapshot_quantity", "Snapshots"],
      ].map(([key, label]) =>
        metric(
          label,
          available(a, (a.counts || {})[key]),
          available(b, (b.counts || {})[key]),
          comparable
        )
      ),
    },
  ];
  const definitions = [
    [
      "charge_currencies",
      "Customer payments",
      [
        ["sales_minor", "Payments"],
        ["refunds_minor", "Refunds"],
      ],
    ],
    [
      "settlement_currencies",
      "Payment processing",
      [
        ["payments_minor", "Payments"],
        ["refunds_minor", "Refunds"],
        ["processing_fees_minor", "Processing fees"],
        ...(includeDisputes
          ? [
              ["dispute_deductions_minor", "Dispute deductions"],
              ["dispute_fees_minor", "Dispute fees"],
              ["dispute_reversals_minor", "Dispute reversals"],
            ]
          : []),
        [
          "net_minor",
          includeDisputes
            ? "Net after fees and adjustments"
            : "Net before dispute adjustments",
        ],
      ],
    ],
  ];
  for (const [key, title, fields] of definitions) {
    const currencies = [
      ...new Set(
        [...(a[key] || []), ...(b[key] || [])].map((row) => row.currency)
      ),
    ].sort();
    for (const code of currencies) {
      const left = (a[key] || []).find((row) => row.currency === code);
      const right = (b[key] || []).find((row) => row.currency === code);
      const currency = left || right;
      const sameScale =
        !left || !right || left.currency_exponent === right.currency_exponent;
      const value = (report, row, field) => {
        const amount = available(report, row ? row[field] : complete(report) ? 0 : null);
        const deduction = ["refunds_minor", "processing_fees_minor", "dispute_deductions_minor", "dispute_fees_minor"].includes(field);
        return amount === null || amount === 0 ? amount : deduction ? -amount : amount;
      };
      groups.push({
        title: `${title} · ${code}`,
        rows: fields.map(([field, label]) => ({
          ...metric(
            label,
            value(a, left, field),
            value(b, right, field),
            comparable &&
              sameScale &&
              (!left || left.complete !== false) &&
              (!right || right.complete !== false),
            currency
          ),
          aCurrency: left || currency,
          bCurrency: right || currency,
        })),
      });
    }
  }
  // Keep money before counts, as in the main Sales Performance report.
  return [...groups.slice(1), groups[0]];
}
function universityComparisons(a, b, includeDisputes) {
  const key = (row) => `${row.tenant}:${row.university_id}`;
  const left = new Map((a.by_university || []).map((row) => [key(row), row]));
  const right = new Map((b.by_university || []).map((row) => [key(row), row]));
  const empty = {
    counts: {
      orders: 0,
      clip_quantity: 0,
      digital_download_quantity: 0,
      snapshot_quantity: 0,
    },
    charge_currencies: [],
    settlement_currencies: [],
  };
  return [...new Set([...left.keys(), ...right.keys()])]
    .map((id) => {
      const row = left.get(id) || right.get(id);
      return {
        id,
        name: row.university_name || row.university_slug,
        tenant: row.tenant,
        groups: comparisonRows(
          { ...empty, ...left.get(id), coverage: a.coverage },
          { ...empty, ...right.get(id), coverage: b.coverage },
          includeDisputes
        ),
      };
    })
    .sort((x, y) => x.name.localeCompare(y.name));
}
function formatValue(value, currency) {
  if (value === null) return "Unavailable";
  if (!currency) return new Intl.NumberFormat("en-GB").format(value);
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currency.currency,
    minimumFractionDigits: currency.currency_exponent,
    maximumFractionDigits: currency.currency_exponent,
  }).format(value / 10 ** currency.currency_exponent);
}
function csvCell(value) {
  let text = value == null ? "" : String(value);
  // Labels can contain university names; prevent spreadsheet formula execution.
  if (typeof value !== "number" && /^[\s]*[=+@-]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}
function comparisonCsv(snapshot) {
  const { reports, params, labels, groups, universities } = snapshot;
  const rows = [
    ["Sales Performance comparison", "A minus B"],
    ["Amounts", "Original minor units; currencies and exponents are explicit"],
    [
      "Coverage",
      "Complete means source coverage, not financial reconciliation",
    ],
    [
      "Refunds and fees",
      "Signed deductions; refunds and fees are negative, reversals are positive",
    ],
  ];
  reports.forEach((report, index) => {
    rows.push(
      [index ? "B" : "A", labels[index]],
      ["Request", JSON.stringify(params[index])],
      ["Coverage", JSON.stringify(report.coverage)],
      ["Freshness", JSON.stringify(report.freshness)],
      ["Generated at", report.generated_at]
    );
  });
  rows.push([
    "Scope",
    "Group",
    "Metric",
    "Currency A",
    "Exponent A",
    "Currency B",
    "Exponent B",
    "A",
    "B",
    "Change A minus B",
    "Change percent",
    "Comparison status",
  ]);
  const append = (scope, sections) =>
    sections.forEach((group) =>
      group.rows.forEach((row) => {
        const aCurrency = row.aCurrency || row.currency;
        const bCurrency = row.bCurrency || row.currency;
        rows.push([
          scope,
          group.title,
          row.label,
          aCurrency && aCurrency.currency,
          aCurrency && aCurrency.currency_exponent,
          bCurrency && bCurrency.currency,
          bCurrency && bCurrency.currency_exponent,
          row.a,
          row.b,
          row.delta,
          row.percent,
          row.reason,
        ]);
      })
    );
  append("All selected universities", groups);
  universities.forEach((university) =>
    append(`${university.tenant}: ${university.name}`, university.groups)
  );
  // Bound the browser-generated aggregate export; never truncate silently.
  if (rows.length > 10000)
    throw new Error(
      "This export is too large. Choose one university and try again."
    );
  return rows.map((row) => row.map(csvCell).join(",")).join("\r\n");
}
module.exports = {
  yearToDate,
  previousPeriod,
  validation,
  requestParams,
  comparisonRows,
  universityComparisons,
  formatValue,
  comparisonCsv,
};
